import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useHiringAccess } from "@/lib/hiringAccess";
import ScheduleInterviewButton from "./hiring/ScheduleInterviewButton";
import PromoteStageButton from "./hiring/PromoteStageButton";
import ExtendOfferButton from "./hiring/ExtendOfferButton";
import ApproveButton from "./hiring/ApproveButton";
import SendStaffMeetingInviteButton from "./hiring/SendStaffMeetingInviteButton";
import RescindApprovalButton from "./hiring/RescindApprovalButton";
import {
  FitCard, AttachmentsPanel, AnalysisHistoryPanel, FullCandidateSummary, useAnalysisPolling,
} from "./hiring/CandidateAttachmentsPanel";
import CollapsibleSummary from "./hiring/CollapsibleSummary";
import BriefAnalysis from "./hiring/BriefAnalysis";
import CallPrepPanel from "./hiring/CallPrepPanel";
import { CallTrackingEditor } from "./hiring/CallTrackingEditor";
import { criterionBrief } from "./hiring/briefAnalysis";
import PanelState, { extractApiError } from "@/components/PanelState";
import {
  ArrowLeft, RefreshCw, Sparkles, AlertTriangle, Clock, User,
  Info, ShieldCheck, Mail, Phone, History, CheckCircle2, XCircle,
  CalendarCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RegionBadge } from "./hiring/RegionBadge";
import { StageTitle, stageLabel, ownersByStage } from "./hiring/stageTitles";

// ============================================================================
// HiringCandidateDetail — Phase D UI (2026-08-24 pt.24).
// Kathryn's Stage 1 review page. Renders candidate detail + workflow
// status + AI OBP annotations (guarded by reviewer_disclaimer).
// Manual Rescore button for platform_admin / owner.
// ============================================================================

const STAGE_LABELS = Object.fromEntries([1, 2, 3, 4, 5].map((n) => [n, stageLabel(n)]));
const STATUS_LABELS = {
  in_progress: "In progress", pass: "Pass", no_pass: "No pass",
  declined: "Declined", offer_extended: "Offer", hired: "Hired", withdrawn: "Withdrawn",
};
const SLA_STATE_META = {
  on_track:            { label: "SLA on track",         color: "text-[#166534] bg-[#dcfce7] border-[#86efac]" },
  sla_breached:        { label: "SLA breached",         color: "text-[#92400e] bg-[#fef3c7] border-[#fbbf24]" },
  tier_1_breached:     { label: "Tier 1 escalated",     color: "text-[#92400e] bg-[#fef3c7] border-[#fbbf24]" },
  tier_2_reassigned:   { label: "Tier 2 reassigned",    color: "text-[#991b1b] bg-[#fee2e2] border-[#f87171]" },
  no_sla:              { label: "No SLA",               color: "text-[#4b5563] bg-[#f3f4f6] border-[#e5e7eb]" },
};

function SectionCard({ title, icon: Icon, testId, right, children }) {
  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e5e7eb] bg-[#f9fafb]">
        <div className="flex items-center gap-2 text-[#0e1420] font-semibold">
          {Icon && <Icon className="w-4 h-4 text-[#4b5563]" />}
          {title}
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function OBPScoreCell({ criterion, value }) {
  if (!value) return <span className="text-[#4b5563]">—</span>;
  // CC-050: verdict + score + first sentence; "Show full analysis" reveals the stored rationale.
  return (
    <div className="text-xs leading-relaxed">
      <BriefAnalysis brief={criterionBrief(criterion, value)} testId={`obp-rationale-${criterion}`}
                     scoreTestId={`obp-score-${criterion}`} />
    </div>
  );
}

// CC-301: same call-tracking record as the Pending applicants row (keyed by the candidate's email).
function CandidateCallTracking({ candidateId }) {
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/hiring/call-tracking/candidate/${candidateId}`).then((r) => setT(r.data)).catch((e) => setErr(extractApiError(e)));
  }, [candidateId]);
  return (
    <SectionCard title="Call tracking" icon={Clock} testId="hiring-candidate-call-tracking">
      {err ? <div className="text-xs text-[#991b1b]">{err.message}</div>
        : t ? <CallTrackingEditor trackKey={t.key} initial={t.call_tracking} testId="candidate-call" />
        : <div className="text-xs text-[#6b7280]">Loading…</div>}
    </SectionCard>
  );
}

export default function HiringCandidateDetail() {
  const { slug, id } = useParams();
  const { user } = useAuth();
  // CC-148 — hiring role OR Settings > Roles stage owner may write (same rule as backend).
  const canWrite = useHiringAccess(user);
  const isPlatformAdmin = user?.role === "platform_admin";
  const isTerminalStatus = (s) => (
    s === "declined" || s === "hired" || s === "withdrawn"
  );

  // Phase G (pt.35) helpers — computed inside render on candidate + roles.
  function _emailsFromRole(roleList, key) {
    const row = (roleList || []).find((r) => r.role_key === key);
    return (row?.primary_user_emails || []).map((e) => e.toLowerCase());
  }
  const userEmail = (user?.email || "").toLowerCase();

  const [state, setState] = useState({ loading: true, error: null });
  const [candidate, setCandidate] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [annotations, setAnnotations] = useState(null);
  const [rescoring, setRescoring] = useState(false);
  const [roles, setRoles] = useState(null);
  // Phase D (2026-09-06) — poll analysis-history after any upload
  // until the newest run's status flips out of "running", then
  // refresh candidate so fit_summary re-renders.
  const { analyzing, refreshKey, start: startAnalysisPolling } =
    useAnalysisPolling({ candidateId: id, onRefresh: () => load() });

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [cand, wf, ann, roleData] = await Promise.all([
        api.get(`/hiring/candidates/${id}`),
        api.get(`/hiring/candidates/${id}/workflow-status`),
        api.get(`/hiring/candidates/${id}/scoring-annotations`),
        api.get(`/hiring/roles`),
      ]);
      setCandidate(cand.data);
      setWorkflow(wf.data);
      setAnnotations(ann.data);
      setRoles(roleData.data?.roles || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: extractApiError(e) });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleRescore() {
    setRescoring(true);
    try {
      const { data } = await api.post(`/hiring/candidates/${id}/rescore`);
      if (data?.status === "disabled") {
        toast("AI scoring is disabled — no re-run performed. Flip HIRING_AI_SCORING_ENABLED to enable.");
      } else if (data?.status === "scored") {
        toast.success("Candidate re-scored");
      } else if (data?.status === "failed") {
        toast.error(`Re-score failed: ${data?.error || "unknown"}`);
      }
      await load();
    } catch (e) {
      toast.error(extractApiError(e) || "Rescore failed");
    } finally {
      setRescoring(false);
    }
  }

  async function handleMarkHired() {
    if (!window.confirm(
      "Mark this candidate as HIRED?\n\n"
      + "This locks the offer to 'accepted' and sends a Google Calendar "
      + "invite for the next Monday staff meeting."
    )) return;
    try {
      const { data } = await api.post(`/hiring/candidates/${id}/mark-hired`);
      if (data?.staff_meeting_invite) {
        toast.success(
          `Marked hired — staff meeting invite sent for `
          + new Date(data.staff_meeting_invite.scheduled_at).toLocaleDateString(),
        );
      } else {
        toast.success("Marked hired — staff meeting invite skipped (see logs)");
      }
      await load();
    } catch (e) {
      toast.error(extractApiError(e) || "Mark hired failed");
    }
  }

  async function handleMarkDeclined() {
    const reason = window.prompt(
      "Mark this candidate as DECLINED (candidate rejected the offer).\n\n"
      + "Optional reason (persisted to audit log):"
    );
    if (reason === null) return; // user cancelled
    try {
      await api.post(
        `/hiring/candidates/${id}/mark-declined`,
        { reason: (reason || "").trim() },
      );
      toast.success("Marked declined");
      await load();
    } catch (e) {
      toast.error(extractApiError(e) || "Mark declined failed");
    }
  }

  if (state.loading || state.error) {
    return (
      <PanelState
        status={state.loading ? "loading" : "error"}
        error={state.error}
        onRetry={load}
        testIdPrefix="hiring-candidate-detail"
      />
    );
  }
  if (!candidate) return null;

  const contact = candidate.contact || {};
  const borderline = (candidate.notes || []).filter((n) => n.kind === "borderline");
  const slaMeta = SLA_STATE_META[workflow?.sla_state] || SLA_STATE_META.no_sla;
  const aiStatus = annotations?.status || "in_progress";

  // Phase G (pt.35) role/approval gates
  const stage4Owners = _emailsFromRole(roles, "stage_4_interviewers");
  const currentOwners = (candidate.current_owner_emails || []).map((e) => e.toLowerCase());
  const isStage4Owner = isPlatformAdmin || stage4Owners.includes(userEmail);
  const isCurrentOwner = isPlatformAdmin || currentOwners.includes(userEmail);
  const approvalTypes = new Set(
    (candidate.approvals || [])
      .filter((a) => !a.rescinded_at)  // pt.36 — rescinded approvals no longer count as active gates
      .map((a) => a.type),
  );
  const hasApproval = (t) => approvalTypes.has(t);

  return (
    <div className="space-y-6" data-testid="hiring-candidate-detail-page">
      <RegionBadge region={candidate.region} size="lg" testId="hiring-candidate-region-badge" />
      <div className="flex items-center gap-3">
        <Link
          to={`/${slug}/app/hiring/candidates`}
          className="text-[#4b5563] hover:text-[#2563eb] inline-flex items-center gap-1"
          data-testid="hiring-candidate-back"
        >
          <ArrowLeft className="w-4 h-4" /> Candidates
        </Link>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#0e1420]" data-testid="hiring-candidate-name">
            {contact.name || "(no name)"}
          </h1>
          <div className="text-sm text-[#4b5563] mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{contact.email || "—"}</span>
            {contact.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{contact.phone}</span>}
            <span>· {candidate.position_title || "Electrician"}</span>
            <span className="inline-flex gap-1">· <StageTitle stage={candidate.stage} owners={ownersByStage(roles)} testId="hiring-candidate-stage-title" /></span>
            <span>· {STATUS_LABELS[candidate.status] || candidate.status}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            data-testid="hiring-candidate-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          {canWrite && (candidate.stage === 3 || candidate.stage === 4) && (
            <ScheduleInterviewButton candidate={candidate} onScheduled={load} />
          )}

          {/* Approver actions — Brett + Nathanael (or platform_admin) */}
          {isStage4Owner && candidate.stage === 4 && candidate.status === "in_progress" && !hasApproval("staff_meeting_invite") && (
            <ApproveButton
              candidate={candidate}
              type="staff_meeting_invite"
              onApproved={load}
            />
          )}
          {isStage4Owner && candidate.stage === 5 && candidate.status === "in_progress" && hasApproval("staff_meeting_invite") && !hasApproval("offer_extension") && (
            <ApproveButton
              candidate={candidate}
              type="offer_extension"
              onApproved={load}
            />
          )}

          {/* Executor actions — current owner (Juliana) once approvals exist */}
          {isCurrentOwner && candidate.stage === 5 && candidate.status === "in_progress" && hasApproval("staff_meeting_invite") && (
            <SendStaffMeetingInviteButton candidate={candidate} onSent={load} />
          )}
          {isCurrentOwner && candidate.stage === 5 && candidate.status === "in_progress" && hasApproval("offer_extension") && (
            <ExtendOfferButton candidate={candidate} onExtended={load} />
          )}

          {canWrite && !isTerminalStatus(candidate.status) && (
            <PromoteStageButton candidate={candidate} onPromoted={load} />
          )}
        </div>
      </div>

      {/* Phase D — Fit summary + attachments (Nathan-unblock priority, 2026-09-06) */}
      <FitCard candidate={candidate} analyzing={analyzing} />
      <FullCandidateSummary candidateId={id} refreshKey={refreshKey} />
      <AttachmentsPanel
        candidate={candidate}
        canWrite={canWrite}
        onUploaded={async () => {
          // Uploader already refreshed the candidate row server-side;
          // pull it locally so the new attachment shows up, then kick
          // off the analyzing… poll for the fit_summary refresh.
          await load();
          startAnalysisPolling();
        }}
        onNoteAdded={async () => { await load(); }}
      />
      <AnalysisHistoryPanel candidateId={id} refreshKey={refreshKey} />
      <CallPrepPanel candidateId={id} canGenerate={isPlatformAdmin || user?.role === "owner"} />

      {/* Borderline note (Section 2 step 7 spec) */}
      {borderline.length > 0 && (
        <div
          className="bg-[#fffbeb] border border-[#fbbf24] rounded-lg p-4 flex gap-3"
          data-testid="hiring-candidate-borderline-note"
        >
          <Info className="w-5 h-5 text-[#b45309] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#78350f]">
            {borderline.map((n) => (
              <div key={n.id}>{n.body}</div>
            ))}
          </div>
        </div>
      )}

      {/* Offer card (Phase F, pt.32) */}
      {candidate.offer && (
        <SectionCard title="Offer" icon={Sparkles} testId="hiring-candidate-offer-card">
          <div className="grid gap-3 md:grid-cols-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Compensation</div>
              <div className="text-[#0e1420] font-medium" data-testid="hiring-offer-compensation">
                {candidate.offer.compensation_type || "—"}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Start date</div>
              <div className="text-[#0e1420]" data-testid="hiring-offer-start">{candidate.offer.start_date || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Expires</div>
              <div className="text-[#0e1420]" data-testid="hiring-offer-expires">{candidate.offer.expiration_date || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Status</div>
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs border ${
                  candidate.offer.status === "accepted" ? "text-[#166534] bg-[#dcfce7] border-[#86efac]"
                    : candidate.offer.status === "declined" ? "text-[#991b1b] bg-[#fee2e2] border-[#f87171]"
                    : candidate.offer.status === "expired" ? "text-[#4b5563] bg-[#f3f4f6] border-[#e5e7eb]"
                    : "text-[#1e40af] bg-[#dbeafe] border-[#93c5fd]"
                }`}
                data-testid="hiring-offer-status"
              >
                {candidate.offer.status}
              </span>
            </div>
          </div>
          {candidate.offer.notes && (
            <div className="mt-3 pt-3 border-t border-[#e5e7eb]">
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Notes / terms</div>
              <div className="text-sm text-[#0e1420] whitespace-pre-wrap">{candidate.offer.notes}</div>
            </div>
          )}
          <div className="mt-3 text-xs text-[#9ca3af]">
            Extended {new Date(candidate.offer.extended_at).toLocaleString()} by {candidate.offer.extended_by_email}
          </div>

          {/* Accept / Decline buttons — only while the offer is still open */}
          {canWrite && candidate.offer.status === "extended" && (
            <div className="mt-4 pt-3 border-t border-[#e5e7eb] flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={handleMarkHired}
                data-testid="hiring-candidate-mark-hired-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark hired
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleMarkDeclined}
                data-testid="hiring-candidate-mark-declined-btn"
              >
                <XCircle className="w-4 h-4 mr-1.5" /> Mark declined
              </Button>
            </div>
          )}

          {/* Staff-meeting shadow-invite chip once mark-hired lands */}
          {candidate.staff_meeting_invite?.scheduled_at && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#166534] bg-[#dcfce7] border border-[#86efac] rounded-full px-3 py-1"
              data-testid="hiring-candidate-staff-meeting-chip"
            >
              <CalendarCheck2 className="w-3.5 h-3.5" />
              Staff meeting invite sent for {new Date(candidate.staff_meeting_invite.scheduled_at).toLocaleDateString()}
            </div>
          )}
        </SectionCard>
      )}

      {/* Workflow / SLA */}
      <CandidateCallTracking candidateId={candidate.id} />
      <SectionCard title="Workflow status" icon={Clock} testId="hiring-candidate-workflow">
        <div className="grid gap-3 md:grid-cols-3 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Current owner</div>
            <div className="text-[#0e1420]">{(workflow?.current_owner_emails || []).join(", ") || "—"}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">SLA state</div>
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${slaMeta.color}`} data-testid="hiring-candidate-sla-state">
              {slaMeta.label}
            </span>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">SLA due</div>
            <div className="text-[#0e1420] text-xs">
              {workflow?.stage_sla_due_at ? new Date(workflow.stage_sla_due_at).toLocaleString() : "—"}
            </div>
          </div>
        </div>
        {(workflow?.tier_1_escalated_at || workflow?.tier_2_reassigned_at) && (
          <div className="mt-3 pt-3 border-t border-[#e5e7eb] text-xs text-[#4b5563]">
            {workflow.tier_1_escalated_at && (
              <div>Tier 1 escalated at {new Date(workflow.tier_1_escalated_at).toLocaleString()}</div>
            )}
            {workflow.tier_2_reassigned_at && (
              <div>Tier 2 reassigned to {workflow.tier_2_reassigned_to_email} at {new Date(workflow.tier_2_reassigned_at).toLocaleString()}</div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Ownership timeline (Phase E, pt.25) */}
      {(workflow?.ownership_history || []).length > 0 && (
        <SectionCard title="Handoff & ownership history" icon={History} testId="hiring-candidate-timeline">
          <ol className="space-y-2 text-sm">
            {[...(workflow.ownership_history || [])].sort((a, b) => (a.at || "").localeCompare(b.at || "")).map((ev) => {
              const iconMap = {
                stage_entered: <ArrowLeft className="w-3.5 h-3.5 rotate-180 text-[#2563eb]" />,
                tier_1_escalated: <AlertTriangle className="w-3.5 h-3.5 text-[#b45309]" />,
                tier_2_reassigned: <RefreshCw className="w-3.5 h-3.5 text-[#dc2626]" />,
                manual_reassignment: <User className="w-3.5 h-3.5 text-[#4b5563]" />,
                approval_granted: <ShieldCheck className="w-3.5 h-3.5 text-[#166534]" />,
                approval_rescinded: <XCircle className="w-3.5 h-3.5 text-[#991b1b]" />,
                staff_meeting_invite_sent: <CalendarCheck2 className="w-3.5 h-3.5 text-[#2563eb]" />,
                offer_extended: <Sparkles className="w-3.5 h-3.5 text-[#2563eb]" />,
                offer_accepted: <CheckCircle2 className="w-3.5 h-3.5 text-[#166534]" />,
                offer_declined: <XCircle className="w-3.5 h-3.5 text-[#dc2626]" />,
              };
              const approvalLabels = {
                staff_meeting_invite: "Approved staff meeting invite",
                offer_extension: "Approved offer extension",
              };
              const rescindLabels = {
                staff_meeting_invite: "Rescinded staff-meeting-invite approval",
                offer_extension: "Rescinded offer-extension approval",
              };
              const label = {
                stage_entered: `Entered ${STAGE_LABELS[ev.to_stage] || `Stage ${ev.to_stage}`}`,
                tier_1_escalated: `Tier 1 alert sent`,
                tier_2_reassigned: `Reassigned to backup owner`,
                manual_reassignment: `Manually reassigned`,
                approval_granted: approvalLabels[ev.reason] || `Approval granted (${ev.reason})`,
                approval_rescinded: rescindLabels[ev.reason] || `Approval rescinded (${ev.reason})`,
                staff_meeting_invite_sent: ev.reason === "executor_send_skipped"
                  ? "Staff meeting invite — send skipped"
                  : "Staff meeting invite sent",
                offer_extended: "Offer extended",
                offer_accepted: "Offer accepted",
                offer_declined: "Offer declined",
              }[ev.event_type] || ev.event_type;
              return (
                <li
                  key={ev.id}
                  className="flex items-start gap-2"
                  data-testid={`hiring-candidate-timeline-event-${ev.event_type}`}
                >
                  <span className="pt-0.5">{iconMap[ev.event_type] || <Info className="w-3.5 h-3.5" />}</span>
                  <div className="flex-1">
                    <div className="text-[#0e1420] flex items-center gap-3">
                      <span>{label}</span>
                      {ev.event_type === "approval_granted" && canWrite && (() => {
                        // Correlate timeline event to approval record by type+timestamp.
                        const approval = (candidate.approvals || []).find(
                          (a) => a.type === ev.reason && a.approved_at === ev.at,
                        );
                        if (!approval) return null;
                        if (approval.rescinded_at) return (
                          <span className="text-xs text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded-full px-2 py-0.5"
                                data-testid={`hiring-timeline-rescinded-chip-${approval.id}`}>
                            Rescinded
                          </span>
                        );
                        return (
                          <RescindApprovalButton
                            candidateId={id}
                            approval={approval}
                            onRescinded={load}
                          />
                        );
                      })()}
                    </div>
                    <div className="text-xs text-[#4b5563]">
                      {ev.to_email && <>Owner: {ev.to_email}</>}
                      {ev.to_emails && ev.to_emails.length > 0 && <>Owner: {ev.to_emails.join(", ")}</>}
                      {ev.from_email && ev.event_type !== "stage_entered" && <> · from {ev.from_email}</>}
                      {ev.from_emails && ev.from_emails.length > 0 && <> · from {ev.from_emails.join(", ")}</>}
                      {ev.reason && ev.event_type !== "approval_granted" && <> · {ev.reason.replace(/_/g, " ")}</>}
                      <> · {new Date(ev.at).toLocaleString()}</>
                    </div>
                    {ev.notes && (
                      <div className="text-xs text-[#4b5563] italic mt-0.5">
                        &ldquo;{ev.notes}&rdquo;
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </SectionCard>
      )}

      {/* AI OBP annotations */}
      <SectionCard
        title="AI OBP screening"
        icon={Sparkles}
        testId="hiring-candidate-ai-panel"
        right={canWrite && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRescore}
            disabled={rescoring}
            data-testid="hiring-candidate-rescore-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${rescoring ? "animate-spin" : ""}`} />
            {rescoring ? "Rescoring…" : "Rescore"}
          </Button>
        )}
      >
        {/* Disclaimer surfaced ABOVE any AI content per pt.25 */}
        <div
          className="mb-4 text-sm text-[#4b5563] bg-[#f3f4f6] border border-[#e5e7eb] rounded p-3 flex gap-2"
          data-testid="hiring-candidate-reviewer-disclaimer"
        >
          <Info className="w-4 h-4 text-[#4b5563] flex-shrink-0 mt-0.5" />
          <span>{annotations?.reviewer_disclaimer || "AI-generated assessment to inform review, not a pass/fail gate."}</span>
        </div>

        {aiStatus === "disabled" && (
          <div className="text-sm text-[#4b5563] bg-[#f3f4f6] border border-[#e5e7eb] rounded p-3">
            AI scoring is not yet enabled. Rescore will be available when the platform admin turns scoring on.
          </div>
        )}
        {aiStatus === "in_progress" && (
          <div className="text-sm text-[#4b5563]">AI screening is in progress — refresh in a few seconds.</div>
        )}
        {aiStatus === "failed" && (
          <div className="text-sm text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-3">
            AI screening failed: {annotations?.error || "unknown error"}. Use Rescore to retry.
          </div>
        )}
        {aiStatus === "scored" && annotations?.obp && (
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Composite scope instinct</div>
              <OBPScoreCell criterion="composite_scope_instinct" value={annotations.obp.composite_scope_instinct} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Discount resistance</div>
              <OBPScoreCell criterion="resistance_to_unauthorized_discounting" value={annotations.obp.resistance_to_unauthorized_discounting} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Whole-home mindset</div>
              <OBPScoreCell criterion="whole_home_mindset" value={annotations.obp.whole_home_mindset} />
            </div>
          </div>
        )}

        {annotations?.ran_at && (
          <div className="mt-4 text-xs text-[#9ca3af]">
            Model: {annotations.model || "—"} · prompt {annotations.prompt_version || "—"} · ran {new Date(annotations.ran_at).toLocaleString()}
          </div>
        )}
      </SectionCard>

      {/* Per-question answers with AI notes */}
      <SectionCard title="Open-ended answers" icon={User} testId="hiring-candidate-open-ended">
        <div className="space-y-4">
          {(annotations?.per_question_annotations || []).map((q) => (
            <div key={q.question_key} className="border-l-2 border-[#e5e7eb] pl-4" data-testid={`hiring-candidate-oe-${q.question_key}`}>
              <div className="text-sm font-medium text-[#0e1420]">{q.question_label}</div>
              <div className="text-xs text-[#4b5563] mt-0.5">Criteria: {(q.obp_criteria || []).join(", ") || "—"}</div>
              <div className="text-sm text-[#0e1420] mt-2 whitespace-pre-wrap">{q.answer || "—"}</div>
              {q.ai_note && (
                <div className="mt-2 text-xs text-[#374151] bg-[#f3f4f6] border border-[#e5e7eb] rounded px-3 py-2 italic">
                  <span className="not-italic font-semibold text-[#4b5563]">AI note:</span>{" "}
                  <CollapsibleSummary text={q.ai_note} testId={`hiring-candidate-oe-${q.question_key}-ai-note`} />
                </div>
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Cultural signal question — always surfaced separately per pt.19 */}
      {annotations?.cultural_signal_only?.length > 0 && (
        <SectionCard title="Cultural signal" icon={User} testId="hiring-candidate-cultural">
          <div className="text-xs text-[#4b5563] mb-3">Not AI-graded — read directly.</div>
          {annotations.cultural_signal_only.map((q) => (
            <div key={q.question_key} className="border-l-2 border-[#e5e7eb] pl-4">
              <div className="text-sm font-medium text-[#0e1420]">{q.question_label}</div>
              <div className="text-sm text-[#0e1420] mt-2 whitespace-pre-wrap">{q.answer || "—"}</div>
            </div>
          ))}
        </SectionCard>
      )}

      {/* Hard-gate answers */}
      <SectionCard title="Quick-check answers" icon={ShieldCheck} testId="hiring-candidate-hard-gate">
        <ul className="space-y-1 text-sm">
          {(candidate.hard_gate_answers || []).map((a) => (
            <li key={a.key} className="flex gap-2">
              <span className="text-[#4b5563]">{a.key}:</span>
              <span className="text-[#0e1420] font-medium">
                {typeof a.answer === "boolean" ? (a.answer ? "Yes" : "No") : String(a.answer)}
              </span>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}
